import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { SERVER } from '../../../../config/config';
import { usePermission } from '../../../../hooks/usePermission';
import { 
  FaPlus, FaEdit, FaTrash, FaToggleOn, FaToggleOff, 
  FaSearch, FaFilter, FaSave, FaTimes, FaStar, FaRegStar, FaUser,
  FaImage, FaVolumeUp
} from 'react-icons/fa';

const Testimonials = () => {
  const { can } = usePermission();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  const [filterFeatured, setFilterFeatured] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [formData, setFormData] = useState({
    name: '',
    title: '',
    company: '',
    image: null,
    existing_image: '', // Add this
    long_image: null,
    existing_long_image: '', // Add this
    audio: null,
    existing_audio: '', // Add this
    rating: 5,
    testimonial: '',
    testimonial_type: 'standard',
    display_order: 0,
    active: true
  });

  const canEdit = can('content.edit');
  const canCreate = can('content.create');
  const canDelete = can('content.delete');

  useEffect(() => {
    fetchTestimonials();
  }, [currentPage, searchTerm, filterRating, filterFeatured, filterType]);

  const fetchTestimonials = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 10,
        search: searchTerm,
        ...(filterRating !== 'all' && { rating: filterRating }),
        ...(filterFeatured !== 'all' && { active: filterFeatured === 'active' }),
        ...(filterType !== 'all' && { type: filterType })
      };

      const response = await axios.get(`${SERVER}/testimonials/admin/list`, {
        params,
        withCredentials: true
      });

      setTestimonials(response.data.testimonials);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate && !editingTestimonial) return;
    if (!canEdit && editingTestimonial) return;

    try {
      const formDataToSend = new FormData();
      
      // Handle regular fields
      Object.keys(formData).forEach(key => {
        if (key === 'image' || key === 'long_image' || key === 'audio') {
          // Only append if it's a new file
          if (formData[key] instanceof File) {
            formDataToSend.append(key, formData[key]);
          }
        } else if (key === 'existing_image' || key === 'existing_long_image' || key === 'existing_audio') {
          // Send existing paths to backend so it knows to keep them
          // Only send if there's an existing path AND no new file was uploaded for this field
          const fieldName = key.replace('existing_', ''); // 'image', 'long_image', or 'audio'
          if (formData[key] && !(formData[fieldName] instanceof File)) {
            formDataToSend.append(key, formData[key]);
          }
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });

      let response;
      if (editingTestimonial) {
        response = await axios.put(`${SERVER}/testimonials/admin/${editingTestimonial.id}`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        response = await axios.post(`${SERVER}/testimonials/admin`, formDataToSend, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      if (response.status === 200 || response.status === 201) {
        fetchTestimonials();
        closeModal();
      }
    } catch (error) {
      console.error('Error saving testimonial:', error);
    }
  };

  const handleEdit = async (testimonial) => {
    if (!canEdit) return;
    
    try {
      const response = await axios.get(`${SERVER}/testimonials/admin/${testimonial.id}`, {
        withCredentials: true
      });
      
      const testimonialData = response.data;
      console.log('Fetched testimonial data for editing:', testimonialData); // Debug log
      
      setFormData({
        name: testimonialData.client_name || '',
        title: testimonialData.title || '',
        company: testimonialData.company || '',
        image: null,
        existing_image: testimonialData.photo || '',
        long_image: null,
        existing_long_image: testimonialData.long_image || '',
        audio: null,
        existing_audio: testimonialData.audio || '',
        rating: testimonialData.rating || 5,
        testimonial: testimonialData.review || '',
        testimonial_type: testimonialData.testimonial_type || 'standard',
        display_order: testimonialData.display_order || 0,
        active: testimonialData.active
      });
      
      console.log('Form data set with existing images:', {
        existing_image: testimonialData.photo,
        existing_long_image: testimonialData.long_image,
        existing_audio: testimonialData.audio
      }); // Debug log
      
      setEditingTestimonial(testimonial);
      setShowModal(true);
    } catch (error) {
      console.error('Error fetching testimonial details:', error);
      alert('Error loading testimonial details. Please try again.');
    }
  };

  const handleDelete = async (testimonial) => {
    if (!canDelete) return;
    
    if (window.confirm(`Are you sure you want to delete the testimonial from "${testimonial.client_name}"?`)) {
      try {
        await axios.delete(`${SERVER}/testimonials/admin/${testimonial.id}`, {
          withCredentials: true
        });
        fetchTestimonials();
      } catch (error) {
        console.error('Error deleting testimonial:', error);
      }
    }
  };

  const handleToggleFeatured = async (testimonial) => {
    if (!canEdit) return;
    
    try {
      await axios.put(`${SERVER}/testimonials/admin/${testimonial.id}/toggle`, {}, {
        withCredentials: true
      });
      fetchTestimonials();
    } catch (error) {
      console.error('Error toggling active status:', error);
    }
  };

  const openCreateModal = () => {
    if (!canCreate) return;
    
    setFormData({
      name: '',
      title: '',
      company: '',
      image: null,
      existing_image: '',
      long_image: null,
      existing_long_image: '',
      audio: null,
      existing_audio: '',
      rating: 5,
      testimonial: '',
      testimonial_type: 'standard',
      display_order: 0,
      active: true
    });
    setEditingTestimonial(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingTestimonial(null);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <FaStar
        key={index}
        className={index < rating ? 'text-yellow-400' : 'text-slate-300'}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Testimonials Management</h2>
          <p className="text-slate-600">Manage client testimonials and reviews</p>
        </div>
        {canCreate && (
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <FaPlus />
            <span>Add Testimonial</span>
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search testimonials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={filterRating}
              onChange={(e) => setFilterRating(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
            <select
              value={filterFeatured}
              onChange={(e) => setFilterFeatured(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Testimonials</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="standard">Standard</option>
              <option value="carousel">Carousel</option>
            </select>
          </div>
        </div>
      </div>

      {/* Testimonials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {testimonials.map((testimonial) => (
          <div key={testimonial.id} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6 hover:shadow-xl transition-all duration-300">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {testimonial.photo ? (
                  <img
                    src={`${SERVER}${testimonial.photo}`}
                    alt={testimonial.client_name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                    <FaUser className="text-slate-400" />
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-slate-800">{testimonial.client_name}</h3>
                  <p className="text-sm text-slate-500">{testimonial.title}</p>
                  {testimonial.company && (
                    <p className="text-xs text-slate-400">{testimonial.company}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleFeatured(testimonial)}
                  disabled={!canEdit}
                  className={`p-2 rounded-lg transition-colors ${
                    testimonial.active
                      ? 'text-green-500 hover:bg-green-50'
                      : 'text-red-400 hover:bg-red-50'
                  } ${!canEdit ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                  title={testimonial.active ? 'Active' : 'Inactive'}
                >
                  {testimonial.active ? <FaToggleOn /> : <FaToggleOff />}
                </button>
                
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  testimonial.active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {testimonial.active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1 mb-3">
              {renderStars(testimonial.rating)}
              <span className="text-sm text-slate-500 ml-2">
                Order: {testimonial.display_order}
              </span>
              <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${
                testimonial.testimonial_type === 'carousel'
                  ? 'bg-purple-100 text-purple-800'
                  : 'bg-blue-100 text-blue-800'
              }`}>
                {testimonial.testimonial_type || 'standard'}
              </span>
            </div>

            <p className="text-slate-600 text-sm mb-4 line-clamp-4">{testimonial.review}</p>

            {/* Media indicators for carousel testimonials */}
            {testimonial.testimonial_type === 'carousel' && (
              <div className="flex items-center gap-2 mb-4">
                {testimonial.long_image && (
                  <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                    <FaImage />
                    Long Image
                  </span>
                )}
                {testimonial.audio && (
                  <span className="flex items-center gap-1 text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded-full">
                    <FaVolumeUp />
                    Audio
                  </span>
                )}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-200">
              <button
                onClick={() => handleEdit(testimonial)}
                disabled={!canEdit}
                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Edit"
              >
                <FaEdit />
              </button>
              <button
                onClick={() => handleDelete(testimonial)}
                disabled={!canDelete}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Delete"
              >
                <FaTrash />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-slate-800">
                  {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter client name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g., CEO, Founder, Manager"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Company name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Testimonial *
                </label>
                <textarea
                  value={formData.testimonial}
                  onChange={(e) => setFormData({ ...formData, testimonial: e.target.value })}
                  required
                  rows={4}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter the client's testimonial"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Rating *
                  </label>
                  <select
                    value={formData.rating}
                    onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value={5}>5 Stars</option>
                    <option value={4}>4 Stars</option>
                    <option value={3}>3 Stars</option>
                    <option value={2}>2 Stars</option>
                    <option value={1}>1 Star</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Testimonial Type *
                  </label>
                  <select
                    value={formData.testimonial_type}
                    onChange={(e) => setFormData({ ...formData, testimonial_type: e.target.value })}
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="standard">Standard</option>
                    <option value="carousel">Carousel</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Client Photo
                </label>
                
                {editingTestimonial && formData.existing_image && (
                  <div className="mb-4">
                    <p className="text-sm text-slate-600 mb-2">Current Photo:</p>
                    <div className="relative inline-block">
                      <img
                        src={`${SERVER}${formData.existing_image}`}
                        alt="Current photo"
                        className="w-24 h-24 object-cover rounded-full border-2 border-slate-300"
                        onError={(e) => {
                          console.error('Failed to load image:', `${SERVER}${formData.existing_image}`);
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full shadow">
                        Current
                      </div>
                    </div>
                  </div>
                )}
                
                {formData.image && (
                  <div className="mb-4">
                    <p className="text-sm text-slate-600 mb-2">New Photo Preview:</p>
                    <div className="relative inline-block">
                      <img
                        src={URL.createObjectURL(formData.image)}
                        alt="New photo preview"
                        className="w-24 h-24 object-cover rounded-full border-2 border-blue-300"
                      />
                      <div className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs px-2 py-1 rounded-full shadow">
                        New
                      </div>
                    </div>
                  </div>
                )}
                
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({ ...formData, image: e.target.files[0] })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {editingTestimonial 
                    ? 'Leave empty to keep current photo. Upload a new photo to replace it.'
                    : 'Standard profile photo for testimonials'}
                </p>
              </div>

              {formData.testimonial_type === 'carousel' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Long Image (Carousel)
                    </label>
                    
                    {editingTestimonial && formData.existing_long_image && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-600 mb-2">Current Long Image:</p>
                        <div className="relative inline-block">
                          <img
                            src={`${SERVER}${formData.existing_long_image}`}
                            alt="Current long image"
                            className="w-32 h-48 object-cover rounded-lg border-2 border-slate-300"
                            onError={(e) => {
                              console.error('Failed to load long image:', `${SERVER}${formData.existing_long_image}`);
                              e.target.style.display = 'none';
                            }}
                          />
                          <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded shadow">
                            Current
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {formData.long_image && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-600 mb-2">New Long Image Preview:</p>
                        <div className="relative inline-block">
                          <img
                            src={URL.createObjectURL(formData.long_image)}
                            alt="New long image preview"
                            className="w-32 h-48 object-cover rounded-lg border-2 border-blue-300"
                          />
                          <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded shadow">
                            New
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setFormData({ ...formData, long_image: e.target.files[0] })}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {editingTestimonial 
                        ? 'Leave empty to keep current long image. Upload a new image to replace it.'
                        : 'Tall rectangular image for carousel display'}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Audio File (Carousel)
                    </label>
                    
                    {editingTestimonial && formData.existing_audio && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-600 mb-2">Current Audio:</p>
                        <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                          <audio controls className="w-full">
                            <source src={`${SERVER}${formData.existing_audio}`} />
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      </div>
                    )}
                    
                    {formData.audio && (
                      <div className="mb-4">
                        <p className="text-sm text-slate-600 mb-2">New Audio Preview:</p>
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <audio controls className="w-full">
                            <source src={URL.createObjectURL(formData.audio)} />
                            Your browser does not support the audio element.
                          </audio>
                        </div>
                      </div>
                    )}
                    
                    <input
                      type="file"
                      accept="audio/*"
                      onChange={(e) => setFormData({ ...formData, audio: e.target.files[0] })}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      {editingTestimonial 
                        ? 'Leave empty to keep current audio. Upload a new audio file to replace it.'
                        : 'Audio testimonial for carousel playback'}
                    </p>
                  </div>
                </>
              )}

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-semibold text-slate-700">Active</span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-6 py-3 text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <FaSave />
                  <span>{editingTestimonial ? 'Update Testimonial' : 'Create Testimonial'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Testimonials;